import React, { useState, type ReactNode } from 'react';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge/lozenge';
import { Popup } from '@atlaskit/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

const styles = cssMap({
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
});

/**
 * Maximum number of characters of the status text rendered inline in the lozenge
 * before it is truncated with an ellipsis (the full value remains available via
 * the popup). Exported so callers that pre-process the status (e.g. the
 * ServiceNow project renderer) can assert against the exact same threshold the
 * component applies, rather than duplicating the magic number.
 */
export const MAX_STATUS_LENGTH = 20;

export interface StatusLozengeButtonProps {
	/** The full status text — shown in the Lozenge (truncated if long), Tooltip, and default popup */
	statusText: string;
	/**
	 * Optional custom popup content. When provided this is shown in the popup instead of the
	 * default plain-text status. Use this for richer content such as "Closed on March 30, 2025".
	 */
	popupContent?: ReactNode;
	/**
	 * Whether the popup is open by default. Primarily useful for VR testing and examples that need
	 * to render the popup open state without user interaction.
	 */
	defaultOpen?: boolean;
	/**
	 * Optional lozenge appearance used to colour-code the status (e.g. `success`, `inprogress`,
	 * `removed`). Defaults to the neutral grey lozenge to preserve existing caller behaviour.
	 */
	appearance?: ThemeAppearance;
}

/**
 * Renders a status badge as a Lozenge inside a clickable Button that opens a Popup with more
 * detail. The status text is truncated in the Lozenge when longer than 20 characters; the full
 * text is always shown in the Tooltip on hover.
 *
 * When `popupContent` is provided it is used as the popup body, allowing callers to show richer
 * information (e.g. a formatted close date for a deal). When omitted the popup simply repeats the
 * full status text.
 */
export const StatusLozengeButton = ({
	statusText,
	popupContent,
	defaultOpen = false,
	appearance,
}: StatusLozengeButtonProps): React.JSX.Element => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	const truncatedStatusText =
		statusText.length > MAX_STATUS_LENGTH
			? `${statusText.slice(0, MAX_STATUS_LENGTH)}...`
			: statusText;

	const defaultPopupContent = (
		<Box xcss={styles.content}>
			<span>{statusText}</span>
		</Box>
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="right-start"
			content={() => popupContent ?? defaultPopupContent}
			trigger={(triggerProps) => (
				<Tooltip content={statusText} position="auto">
					<Button
						{...triggerProps}
						isSelected={isOpen}
						appearance="subtle"
						spacing="compact"
						onClick={() => setIsOpen(!isOpen)}
					>
						<Lozenge appearance={appearance}>{truncatedStatusText}</Lozenge>
					</Button>
				</Tooltip>
			)}
		/>
	);
};
